import { useEffect, useState } from "react"
import PropTypes from "prop-types"

import logIFTTTNextAction from "app/lib/ifttt_next_log_action"
import Steps from "./steps"
import PlatformSelection from "./platform_selection"
import ServiceSelection from "./service_selection"
import Upsell from "./upsell"
import RecommendedApplets from "./recommended_applets"
import { submitChannels } from "./api"

import { useCookies } from "react-cookie"
import { proDetailsPopupCookieName } from "shared/components/pro_details_popup"
import { submitPreferredPlatforms } from "./api"

const OnboardingRecommendations = ({
  browserPlatform,
  channels,
  trialConfig,
  platformPrefImages,
  urls,
  userId,
  newUserOnboarding,
}) => {
  const [selectedChannels, setSelectedChannels] = useState(new Set())
  const cookieName = proDetailsPopupCookieName(userId)
  const [cookies, setCookie] = useCookies([cookieName])

  const isNewUserOnboarding = newUserOnboarding && !cookies[cookieName]

  useEffect(() => {
    if (isNewUserOnboarding && browserPlatform !== "desktop") {
      let platform = null
      switch (browserPlatform) {
        case "ios":
          platform = "iphone"
          break
        case "android":
          platform = "android"
      }
      platform && submitPreferredPlatforms([platform])
    }
  }, [browserPlatform, isNewUserOnboarding])

  const initialStep =
    isNewUserOnboarding && browserPlatform === "desktop"
      ? Steps.PLATFORM_SELECTION
      : Steps.SERVICE_SELECTION
  const [step, setStep] = useState(initialStep)

  const channelSelected = moduleName => selectedChannels.has(moduleName)

  const toggleChannel = moduleName => {
    let newChannels = new Set(selectedChannels)
    if (channelSelected(moduleName)) {
      newChannels.delete(moduleName)
    } else {
      newChannels.add(moduleName)
    }
    setSelectedChannels(newChannels)
  }

  const onPlatformSelected = () => setStep(Steps.SERVICE_SELECTION)

  const onNext = async () => {
    const success = await submitChannels(Array.from(selectedChannels))
    if (success) {
      logIFTTTNextAction("statechange", {
        object_type: "user",
        location_type: newUserOnboarding
          ? "onboarding_selector"
          : "applet_wizard",
        state: "submitted_onboarding",
      })
    }

    if (isNewUserOnboarding) {
      setStep(Steps.UPSELL)
    } else {
      setStep(Steps.RECOMMENDED_APPLETS)
    }
  }

  const onSkipPro = async () => {
    setCookie(cookieName, true)
    setStep(Steps.RECOMMENDED_APPLETS)
  }

  const onTryProClick = async () => {
    setCookie(cookieName, true)
    logIFTTTNextAction("click", {
      object_type: "try_pro_button",
      location_type: "onboarding_selector",
    })
  }

  switch (step) {
    case Steps.PLATFORM_SELECTION:
      return (
        <PlatformSelection
          onPlatformSelected={onPlatformSelected}
          images={platformPrefImages}
        />
      )
    case Steps.SERVICE_SELECTION:
      return (
        <ServiceSelection
          channels={channels}
          onNext={onNext}
          showFooter={!!selectedChannels.size}
          channelSelected={channelSelected}
          toggleChannel={toggleChannel}
          newUserOnboarding={newUserOnboarding}
        />
      )
    case Steps.UPSELL:
      return (
        <Upsell
          onSkip={onSkipPro}
          onTryProClick={onTryProClick}
          trialConfig={trialConfig}
        />
      )
    case Steps.RECOMMENDED_APPLETS:
      return (
        <RecommendedApplets newUserOnboarding={newUserOnboarding} urls={urls} />
      )
  }
}

OnboardingRecommendations.propTypes = {
  channels: PropTypes.array.isRequired,
  newUserOnboarding: PropTypes.bool,
  platformPrefImages: PropTypes.object.isRequired,
  trialConfig: PropTypes.object.isRequired,
  urls: PropTypes.shape({
    exploreUrl: PropTypes.string.isRequired,
  }),
  userId: PropTypes.string.isRequired,
  browserPlatform: PropTypes.string.isRequired,
}

export default OnboardingRecommendations
